// ParkSmart Pro v1.7 — Data Layer
// Identical function signatures to v1.5 — screens need ZERO changes
// All data fetched from backend API, cached in localStorage for offline use
export const APP_VERSION = 'v1.7.0';

const BASE = '';// same origin — nginx proxies /api to backend

// ── CACHE HELPERS ─────────────────────────────────────────
const c = {
  get:(k,def=[])=>{try{const v=localStorage.getItem(k);return v?JSON.parse(v):def;}catch{return def;}},
  set:(k,v)=>{try{localStorage.setItem(k,JSON.stringify(v));}catch{}}
};

// ── API HELPER ────────────────────────────────────────────
const call = async(path,opts={})=>{
  try{
    const res = await fetch(BASE+path,{
      method:opts.method||'GET',
      headers:{'Content-Type':'application/json'},
      body:opts.body?JSON.stringify(opts.body):undefined
    });
    if(!res.ok)return null;
    return await res.json();
  }catch(e){
    console.warn('API offline:',path);
    return null;
  }
};

// ── SETTINGS ─────────────────────────────────────────────
const DEF_SETTINGS = {businessName:'Al Barakah Car Parking',address:'Arabian Gulf Street, Near NMC Royal Hospital, Sharjah',phone:'',totalSpots:50,hourlyRate:8,currency:'AED',overtimeBufferMins:10,overtimeBufferFee:2,receiptFooter:'Thank you for parking with Al Barakah!',rateEditingLocked:false,expiryReminderDays:3};
export const getSettings = () => c.get('ps_settings',DEF_SETTINGS);
export const updateSettings = async(u) => {
  const n={...getSettings(),...u};
  c.set('ps_settings',n);
  await call('/api/settings',{method:'PUT',body:u});
  return n;
};
export const syncSettings = async() => {
  const r = await call('/api/settings');
  if(r && Object.keys(r).length>0){c.set('ps_settings',{...DEF_SETTINGS,...r});}
  return getSettings();
};

// ── AUTH ──────────────────────────────────────────────────
export const authenticateUser = async(username,password) => {
  const r = await call('/api/auth/login',{method:'POST',body:{username,password}});
  if(!r)return null;
  if(r.locked)return{locked:true,error:r.error};
  if(r.success&&r.user)return r.user;
  return null;
};

// ── USERS ─────────────────────────────────────────────────
export const getUsers = async() => {
  const r = await call('/api/users');
  if(r){c.set('ps_users',r);return r;}
  return c.get('ps_users',[]);
};
export const createUser = async(d) => call('/api/users',{method:'POST',body:d});
export const updateUser = async(id,d) => call('/api/users/'+id,{method:'PUT',body:d});
export const deleteUser = async(id) => call('/api/users/'+id,{method:'DELETE'});

// ── TENANTS ───────────────────────────────────────────────
const normTenant = t => ({
  ...t,
  plates: Array.isArray(t.plates)?t.plates:(typeof t.plates==='string'?JSON.parse(t.plates||'[]'):[]),
  notes: Array.isArray(t.notes)?t.notes:(typeof t.notes==='string'?JSON.parse(t.notes||'[]'):[]),
  packageType: t.package_type||t.packageType,
  monthlyRate: t.monthly_rate||t.monthlyRate,
  weeklyRate: t.weekly_rate||t.weeklyRate,
  biweeklyRate: t.biweekly_rate||t.biweeklyRate,
  allowedStartTime: t.allowed_start_time||t.allowedStartTime,
  allowedEndTime: t.allowed_end_time||t.allowedEndTime,
  expiryDate: t.expiry_date||t.expiryDate,
  lastPaidDate: t.last_paid_date||t.lastPaidDate,
  autoRemind: t.auto_remind||t.autoRemind,
});
export const getTenants = async() => {
  const r = await call('/api/tenants');
  if(r){const n=r.map(normTenant);c.set('ps_tenants',n);return n;}
  return c.get('ps_tenants',[]);
};
export const getTenantById = (id) => c.get('ps_tenants',[]).find(t=>t.id===id);
export const getTenantByPlate = (plate) => c.get('ps_tenants',[]).find(t=>t.plates&&t.plates.some(p=>p.toLowerCase()===plate.toLowerCase()));
export const createTenant = async(d) => {
  const r = await call('/api/tenants',{method:'POST',body:{...d,plates:JSON.stringify(d.plates||[]),notes:JSON.stringify(d.notes||[])}});
  await getTenants();return r;
};
export const updateTenant = async(id,d) => {
  const r = await call('/api/tenants/'+id,{method:'PUT',body:{...d,plates:JSON.stringify(d.plates||[]),notes:JSON.stringify(d.notes||[])}});
  await getTenants();return r;
};
export const deleteTenant = async(id) => {const r=await call('/api/tenants/'+id,{method:'DELETE'});await getTenants();return r;};
export const renewTenant = async(id,newExpiryDateStr,staffId,newPkgType) => {
  const t = getTenantById(id); if(!t)return null;
  const expiry = new Date(newExpiryDateStr); expiry.setHours(23,59,59,0);
  return updateTenant(id,{...t,expiryDate:expiry.toISOString(),package_type:newPkgType||t.packageType,last_paid_date:new Date().toISOString(),last_renewed_by:staffId});
};
export const addTenantNote = async(id,note,staffId,staffName) => {
  const t=getTenantById(id);if(!t)return;
  const notes=[...(t.notes||[]),{id:`note_${Date.now()}`,text:note,staffId,staffName,timestamp:new Date().toISOString()}];
  return updateTenant(id,{...t,notes});
};
export const isTenantTimeViolation = (tenant,timeStr) => {
  if(!tenant)return false;
  const pkg=tenant.packageType||tenant.package_type;
  if(pkg==='monthly-24hr')return false;
  const start=tenant.allowedStartTime||tenant.allowed_start_time;
  const end=tenant.allowedEndTime||tenant.allowed_end_time;
  if(!start||!end)return false;
  const[h,m]=timeStr.split(':').map(Number);const mins=h*60+m;
  const[sh,sm]=start.split(':').map(Number);const[eh,em]=end.split(':').map(Number);
  const s=sh*60+sm;const e=eh*60+em;
  if(s<=e)return mins<s||mins>e;
  return mins<s&&mins>e;
};

// ── VEHICLES ──────────────────────────────────────────────
const normVehicle = v => ({
  ...v,
  entryTime: v.entry_time||v.entryTime,
  exitTime: v.exit_time||v.exitTime,
  packageType: v.package_type||v.packageType,
  carType: v.car_type||v.carType,
  tenantId: v.tenant_id||v.tenantId,
  isMou: v.is_mou===1||v.isMou===true,
  mouId: v.mou_id||v.mouId,
  mouCompanyName: v.mou_company_name||v.mouCompanyName,
  hourlyRate: v.hourly_rate||v.hourlyRate||8,
  overnightRate: v.overnight_rate||v.overnightRate,
  overnightEndTime: v.overnight_end_time||v.overnightEndTime,
  overnightAdvance: v.overnight_advance||v.overnightAdvance||0,
  customerPhone: v.customer_phone||v.customerPhone,
  staffId: v.staff_id||v.staffId,
  staffName: v.staff_name||v.staffName,
  exitStaffId: v.exit_staff_id||v.exitStaffId,
  exitStaffName: v.exit_staff_name||v.exitStaffName,
  discountAmount: v.discount_amount||v.discountAmount||0,
  discountReason: v.discount_reason||v.discountReason,
  grossFee: v.gross_fee||v.grossFee||0,
  paymentMethod: v.payment_method||v.paymentMethod,
  forceExited: v.force_exited===1||v.forceExited===true,
});
export const syncVehicles = async() => {
  const r = await call('/api/vehicles');
  if(r){const n=r.map(normVehicle);c.set('ps_vehicles',n);return n;}
  return c.get('ps_vehicles',[]);
};
export const getVehicles = () => c.get('ps_vehicles',[]);
export const getActiveVehicles = () => getVehicles().filter(v=>v.status==='parked');
export const getActiveVehicleByPlate = (plate) => getActiveVehicles().find(v=>v.plate.toLowerCase()===plate.toLowerCase());
export const logVehicleEntry = async(d) => {
  const r = await call('/api/vehicles/entry',{method:'POST',body:{
    plate:d.plate,packageType:d.packageType,carType:d.carType,
    entryTime:d.entryTime,hourlyRate:d.hourlyRate,tenantId:d.tenantId,
    isMou:d.isMou,mouId:d.mouId,mouCompanyName:d.mouCompanyName,
    overnightRate:d.overnightRate,overnightEndTime:d.overnightEndTime,
    overnightAdvance:d.overnightAdvance||0,customerPhone:d.customerPhone,
    staffId:d.staffId,staffName:d.staffName,notes:d.notes
  }});
  // Return immediately — sync in background so screen never blocks
  const entryRecord = {
    ...d,
    id:r?.id||`local_${Date.now()}`,
    entryTime:r?.entryTime||d.entryTime||new Date().toISOString(),
    status:'parked'
  };
  syncVehicles(); // non-blocking background sync
  return entryRecord;
};
export const logVehicleExit = async(id,d) => {
  const r = await call('/api/vehicles/'+id+'/exit',{method:'PUT',body:{
    fee:d.fee,grossFee:d.grossFee,discountAmount:d.discountAmount,
    discountReason:d.discountReason,paymentMethod:d.paymentMethod,
    exitStaffId:d.exitStaffId,exitStaffName:d.exitStaffName,
    duration:d.duration,exitTime:d.exitTime
  }});
  const exitRecord = r?.vehicle||{...getVehicles().find(v=>v.id===id),...d,status:'exited'};
  syncVehicles(); // non-blocking background sync
  return exitRecord;
};
export const forceExitVehicle = async(id,adminId,adminName,reason) => {
  return logVehicleExit(id,{exitTime:new Date().toISOString(),fee:0,paymentMethod:'force_exit',forceExited:true,forceExitReason:reason,exitStaffId:adminId,exitStaffName:adminName});
};

// ── FEE CALCULATORS ───────────────────────────────────────
export const calculateFee = (entryTime,exitTime,hourlyRate=8,discountAmount=0) => {
  const st=getSettings();const bufMins=st.overtimeBufferMins||10;const bufFee=st.overtimeBufferFee||2;
  const durationMins=Math.max(0,Math.floor((new Date(exitTime)-new Date(entryTime))/60000));
  let fee=0;
  if(durationMins<=60){fee=hourlyRate;}
  else{const extra=durationMins-60;const fullH=Math.floor(extra/60);const rem=extra%60;fee=hourlyRate*(fullH+1);if(rem>0&&rem<=bufMins)fee+=bufFee;else if(rem>bufMins)fee+=hourlyRate;}
  return{durationMins,hours:Math.floor(durationMins/60),mins:durationMins%60,grossFee:fee,discount:discountAmount,finalFee:Math.max(0,fee-discountAmount),breakdown:`${Math.floor(durationMins/60)}h ${durationMins%60}m`};
};
export const calculateLiveFee = (entryTime,hourlyRate=8) => calculateFee(entryTime,new Date().toISOString(),hourlyRate,0);
export const calculateOvernightFee = (entryTime,exitTime,agreedRate,agreedEndTime,hourlyRate=8) => {
  const exit=new Date(exitTime);const[endH,endM]=agreedEndTime.split(':').map(Number);
  const agreedEnd=new Date(entryTime);agreedEnd.setHours(endH,endM,0,0);
  if(agreedEnd<=new Date(entryTime))agreedEnd.setDate(agreedEnd.getDate()+1);
  if(exit<=agreedEnd)return{agreedRate,extraFee:0,totalFee:agreedRate,extraMins:0,agreedEndTime,balanceDue:0};
  const extraMins=Math.floor((exit-agreedEnd)/60000);
  const st=getSettings();const buf=st.overtimeBufferMins||10;const bufFee=st.overtimeBufferFee||2;
  const fullH=Math.floor(extraMins/60);const rem=extraMins%60;
  let extraFee=hourlyRate*fullH;if(rem>0&&rem<=buf)extraFee+=bufFee;else if(rem>buf)extraFee+=hourlyRate;
  return{agreedRate,extraFee,totalFee:agreedRate+extraFee,extraMins,agreedEndTime,balanceDue:extraFee};
};

// ── MOU ───────────────────────────────────────────────────
const normMou = m => ({...m,companyName:m.company_name||m.companyName,ratePerHour:m.rate_per_hour||m.ratePerHour||5,contactPerson:m.contact_person||m.contactPerson});
const normMouR = r => ({...r,mouId:r.mou_id||r.mouId,mouCompanyName:r.mou_company_name||r.mouCompanyName,entryTime:r.entry_time||r.entryTime,exitTime:r.exit_time||r.exitTime,settledAt:r.settled_at||r.settledAt,collectedBy:r.collected_by||r.collectedBy});
export const syncMou = async() => {
  const [companies,receivables] = await Promise.all([call('/api/mou/companies'),call('/api/mou/receivables')]);
  if(companies){c.set('ps_mou_companies',companies.map(normMou));}
  if(receivables){c.set('ps_mou_receivables',receivables.map(normMouR));}
};
export const getMouCompanies = () => c.get('ps_mou_companies',[]);
export const getMouById = (id) => getMouCompanies().find(m=>m.id===id);
export const createMou = async(d) => {const r=await call('/api/mou/companies',{method:'POST',body:d});await syncMou();return r;};
export const updateMou = async(id,d) => {await call('/api/mou/companies/'+id,{method:'PUT',body:d});await syncMou();};
export const deleteMou = async(id) => {await call('/api/mou/companies/'+id,{method:'PUT',body:{active:false}});await syncMou();};
export const getMouReceivables = () => c.get('ps_mou_receivables',[]);
export const addMouReceivable = async(d) => {const r=await call('/api/mou/receivables',{method:'POST',body:d});await syncMou();return r;};
export const settleMouReceivable = async(ids,by) => {await call('/api/mou/receivables/settle',{method:'PUT',body:{ids,by}});await syncMou();};
export const getUnsettledMouByCompany = (mouId) => getMouReceivables().filter(r=>r.mouId===mouId&&!r.settled);

// ── EXPENSES ──────────────────────────────────────────────
const normExp = e => ({...e,categoryId:e.category_id||e.categoryId,recordedBy:e.recorded_by||e.recordedBy,createdAt:e.created_at||e.createdAt});
export const syncExpenses = async() => {
  const [expenses,cats] = await Promise.all([call('/api/expenses'),call('/api/expense-categories')]);
  if(expenses)c.set('ps_expenses',expenses.map(normExp));
  if(cats)c.set('ps_expense_categories',cats);
};
export const getExpenses = () => c.get('ps_expenses',[]);
export const addExpense = async(d) => {const r=await call('/api/expenses',{method:'POST',body:d});await syncExpenses();return r;};
export const getExpenseCategories = () => c.get('ps_expense_categories',[]);
export const addExpenseCategory = async(name) => {const r=await call('/api/expense-categories',{method:'POST',body:{name}});await syncExpenses();return r;};
export const deleteExpenseCategory = async(id) => {await call('/api/expense-categories/'+id,{method:'DELETE'});await syncExpenses();};

// ── TIPS ──────────────────────────────────────────────────
const normTip = t => ({...t,staffId:t.staff_id||t.staffId,staffName:t.staff_name||t.staffName,vehicleId:t.vehicle_id||t.vehicleId});
export const syncTips = async() => {const r=await call('/api/tips');if(r)c.set('ps_tips',r.map(normTip));};
export const getTips = () => c.get('ps_tips',[]);
export const addTip = async(d) => {const r=await call('/api/tips',{method:'POST',body:d});await syncTips();return r;};

// ── SHIFTS ────────────────────────────────────────────────
const normShift = s => ({...s,staffId:s.staff_id||s.staffId,staffName:s.staff_name||s.staffName,openedAt:s.opened_at||s.openedAt,closedAt:s.closed_at||s.closedAt,cashHandover:s.cash_handover||s.cashHandover,tipsHandover:s.tips_handover||s.tipsHandover,adminConfirmed:s.admin_confirmed===1||s.adminConfirmed===true,confirmedBy:s.confirmed_by||s.confirmedBy,confirmedAt:s.confirmed_at||s.confirmedAt});
export const syncShifts = async() => {const r=await call('/api/shifts');if(r)c.set('ps_shifts',r.map(normShift));};
export const getShifts = () => c.get('ps_shifts',[]);
export const getOpenShift = (staffId) => getShifts().find(sh=>sh.staffId===staffId&&!sh.closedAt);
export const openShift = async(staffId,staffName) => {const r=await call('/api/shifts',{method:'POST',body:{staffId,staffName}});await syncShifts();return r;};
export const closeShift = async(shiftId,d) => {await call('/api/shifts/'+shiftId+'/close',{method:'PUT',body:d});await syncShifts();};
export const confirmShiftHandover = async(shiftId,adminId) => {await call('/api/shifts/'+shiftId+'/confirm',{method:'PUT',body:{adminId}});await syncShifts();};

// ── CASH CHECKPOINTS ──────────────────────────────────────
const normChk = c => ({...c,staffId:c.staff_id||c.staffId,staffName:c.staff_name||c.staffName});
export const syncCheckpoints = async() => {const r=await call('/api/checkpoints');if(r)c.set('ps_checkpoints',r.map(normChk));};
export const getCashCheckpoints = () => c.get('ps_checkpoints',[]);
export const getTodayCheckpoints = () => {const t=new Date().toISOString().split('T')[0];return getCashCheckpoints().filter(ch=>(ch.timestamp||'').startsWith(t));};
export const addCashCheckpoint = async(d) => {const r=await call('/api/checkpoints',{method:'POST',body:d});await syncCheckpoints();return r;};

// ── BANK ──────────────────────────────────────────────────
export const syncBank = async() => {const r=await call('/api/bank');if(r)c.set('ps_bank',r);};
export const getBankEntries = () => c.get('ps_bank',[]);
export const addBankEntry = async(d) => {const r=await call('/api/bank',{method:'POST',body:d});await syncBank();return r;};
export const getLatestBankBalance = () => {const e=getBankEntries().sort((a,b)=>new Date(b.created_at)-new Date(a.created_at));return e.length?e[0].balance:0;};

// ── CUSTOMERS ─────────────────────────────────────────────
export const syncCustomers = async() => {const r=await call('/api/customers');if(r)c.set('ps_customers',r);};
export const getCustomers = () => c.get('ps_customers',[]);
export const getCustomerByPlate = (plate) => c.get('ps_customers',[]).find(cu=>cu.plate.toLowerCase()===plate.toLowerCase())||null;
export const upsertCustomer = () => {};// handled server-side

// ── AUDIT LOG ─────────────────────────────────────────────
export const getAuditLog = async() => {const r=await call('/api/audit');return r||[];};
export const addAuditEntry = async() => {};// handled server-side

// ── SYNC ALL ──────────────────────────────────────────────
export const initDB = async() => {
  await syncSettings();
  await Promise.all([syncVehicles(),syncMou(),syncExpenses(),syncTips(),syncShifts(),syncCheckpoints(),syncBank(),syncCustomers()]);
  await getTenants();
};

// ── 5-SECOND AUTO POLLING ─────────────────────────────────
// Dispatches a custom event after each sync so React components can re-render
let _pollingInterval = null;
export const startPolling = () => {
  if(_pollingInterval)return;
  _pollingInterval = setInterval(async()=>{
    try{
      const r = await call('/api/vehicles?status=parked');
      if(r){
        const normalised = r.map(normVehicle);
        c.set('ps_vehicles_cache_parked', normalised);
        // Merge with full cache
        const full = c.get('ps_vehicles',[]);
        const ids = new Set(normalised.map(v=>v.id));
        const exited = full.filter(v=>v.status!=='parked');
        c.set('ps_vehicles',[...normalised,...exited]);
        // Dispatch event so Dashboard re-renders
        window.dispatchEvent(new CustomEvent('ps_vehicles_updated'));
      }
    }catch{}
  },2000);
};
export const stopPolling = () => {
  if(_pollingInterval){clearInterval(_pollingInterval);_pollingInterval=null;}
};

// normVehicle defined above in VEHICLES section

// ── REPORTS (derived from cached data) ───────────────────
export const getDailyRevenue = (date) => {
  const ds=date||new Date().toISOString().split('T')[0];
  const vs=getVehicles().filter(v=>v.status!=='parked'&&(v.entryTime||'').startsWith(ds));
  let cash=0,card=0,mou=0;
  vs.forEach(v=>{const m=v.paymentMethod;const f=v.fee||0;if(m==='cash')cash+=f;else if(m==='card')card+=f;else if(m==='mou')mou+=f;});
  return{totalCash:cash,totalCard:card,totalMou:mou,total:cash+card+mou,vehicles:vs};
};
export const getFootfallToday = () => {
  const today=new Date().toISOString().split('T')[0];
  const vs=getVehicles().filter(v=>(v.entryTime||'').startsWith(today));
  const ids=new Set();let hourly=0,mou=0;
  vs.forEach(v=>{if(v.tenantId)ids.add(v.tenantId);else if(v.isMou)mou++;else hourly++;});
  return{fixed:ids.size,hourly,mou,total:ids.size+hourly+mou};
};
export const getOccupancy = () => {
  const st=getSettings();const active=getActiveVehicles();
  return{total:st.totalSpots,occupied:active.length,available:Math.max(0,st.totalSpots-active.length),overCapacity:active.length>st.totalSpots,open:active.filter(v=>v.carType==='open').length,closed:active.filter(v=>v.carType==='closed').length,activeVehicles:active};
};
export const getExpiringTenants = (days=2) => {const cut=new Date();cut.setDate(cut.getDate()+days);return c.get('ps_tenants',[]).filter(t=>t.expiryDate&&t.active&&new Date(t.expiryDate)<=cut);};
export const getDiscountedTransactions = () => getVehicles().filter(v=>v.discountAmount>0);
export const getGhostCars = () => {const cut=new Date();cut.setHours(cut.getHours()-24);return getActiveVehicles().filter(v=>new Date(v.entryTime)<cut);};
export const getTimeViolations = () => [];
export const getAttendanceReport = () => getShifts().filter(sh=>sh.closedAt).map(sh=>({...sh,hoursWorked:sh.closedAt&&sh.openedAt?((new Date(sh.closedAt)-new Date(sh.openedAt))/3600000).toFixed(2):0}));
export const getDailyPL = (date) => {const rev=getDailyRevenue(date);const ds=date||new Date().toISOString().split('T')[0];const exps=getExpenses().filter(e=>(e.date||'').startsWith(ds));const totalExp=exps.reduce((s,e)=>s+(parseFloat(e.amount)||0),0);return{revenue:rev.total,expenses:totalExp,tips:0,netProfit:rev.total-totalExp,cashRevenue:rev.totalCash,cardRevenue:rev.totalCard,mouRevenue:rev.totalMou};};
export const getMonthlyStatement = (year,month) => {const y=year||new Date().getFullYear();const m=month!==undefined?month:new Date().getMonth();const vehicles=getVehicles().filter(v=>{if(!v.entryTime)return false;const d=new Date(v.entryTime);return d.getFullYear()===y&&d.getMonth()===m&&v.status!=='parked';});const revenue=vehicles.reduce((s,v)=>s+(v.fee||0),0);const expenses=getExpenses().filter(e=>{const d=new Date(e.createdAt||e.date);return d.getFullYear()===y&&d.getMonth()===m;});const totalExp=expenses.reduce((s,e)=>s+(parseFloat(e.amount)||0),0);return{revenue,expenses:totalExp,netProfit:revenue-totalExp,totalVisits:vehicles.length,byCategory:{},month:new Date(y,m).toLocaleString('default',{month:'long',year:'numeric'})};};
export const getMouAgingReport = () => {const now=new Date();return getMouReceivables().filter(r=>!r.settled).map(r=>{const days=Math.floor((now-new Date(r.createdAt||r.created_at))/86400000);return{...r,ageDays:days,ageCategory:days<=3?'0-3 days':days<=7?'4-7 days':days<=14?'8-14 days':'15+ days'};});};
export const getCashReconciliation = (date) => {const ds=date||new Date().toISOString().split('T')[0];const rev=getDailyRevenue(ds);const checkpoints=getCashCheckpoints().filter(ch=>(ch.timestamp||'').startsWith(ds));const shifts=getShifts().filter(sh=>(sh.openedAt||'').startsWith(ds));const declared=shifts.filter(sh=>sh.cashHandover!=null).reduce((s,sh)=>s+(sh.cashHandover||0),0);return{expectedCash:rev.totalCash,declaredCash:declared,variance:declared-rev.totalCash,checkpoints,shifts,date:ds};};
export const getTenantVisitFrequency = () => c.get('ps_tenants',[]).map(t=>({...t,visitsThisMonth:0,totalVisits:0,lifetimeValue:0}));
export const getOccupancyHistory = () => Array.from({length:14},(_,i)=>{const d=new Date();d.setDate(d.getDate()-(13-i));const ds=d.toISOString().split('T')[0];const entries=getVehicles().filter(v=>(v.entryTime||'').startsWith(ds));return{date:ds,label:`${d.getDate()}/${d.getMonth()+1}`,visits:entries.length,peakOccupancy:entries.length,utilizationPct:Math.min(100,Math.round((entries.length/(getSettings().totalSpots||50))*100))};});
export const getExpenseTrends = () => [];
export const getPeakHoursData = () => {const hours=Array(24).fill(0);getVehicles().filter(v=>v.entryTime).forEach(v=>{const h=new Date(v.entryTime).getHours();hours[h]++;});return hours.map((count,h)=>({hour:h,label:`${String(h).padStart(2,'0')}:00`,count,avg:count}));};
export const getRevenueByDayOfWeek = () => {const days=['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];const totals=Array(7).fill(0);const counts=Array(7).fill(0);getVehicles().filter(v=>v.exitTime&&v.fee>0).forEach(v=>{const d=new Date(v.entryTime).getDay();totals[d]+=v.fee||0;counts[d]++;});return days.map((name,i)=>({name,total:parseFloat(totals[i].toFixed(2)),visits:counts[i]}));};
export const get30DayRevenue = () => Array.from({length:30},(_,i)=>{const d=new Date();d.setDate(d.getDate()-(29-i));const ds=d.toISOString().split('T')[0];const vs=getVehicles().filter(v=>(v.entryTime||'').startsWith(ds)&&v.status!=='parked');return{date:ds,label:`${d.getDate()}/${d.getMonth()+1}`,total:vs.reduce((s,v)=>s+(v.fee||0),0),visits:vs.length};});
export const getAvgDurationByPackage = () => {const pkgs={};getVehicles().filter(v=>v.exitTime&&v.entryTime).forEach(v=>{const pkg=v.packageType||'hourly';const mins=Math.floor((new Date(v.exitTime)-new Date(v.entryTime))/60000);if(!pkgs[pkg])pkgs[pkg]={total:0,count:0,revenue:0};pkgs[pkg].total+=mins;pkgs[pkg].count++;pkgs[pkg].revenue+=v.fee||0;});return Object.entries(pkgs).map(([pkg,d])=>({pkg,avgMins:d.count?Math.round(d.total/d.count):0,visits:d.count,avgRevenue:d.count?parseFloat((d.revenue/d.count).toFixed(2)):0}));};
export const getStaffPerformance = () => {const staff={};getVehicles().forEach(v=>{const id=v.staffId;if(!id)return;if(!staff[id])staff[id]={staffId:id,staffName:v.staffName||'Unknown',entries:0,exits:0,cashCollected:0,discountsGiven:0,tips:0};staff[id].entries++;if(v.exitStaffId){if(!staff[v.exitStaffId])staff[v.exitStaffId]={staffId:v.exitStaffId,staffName:v.exitStaffName||'Unknown',entries:0,exits:0,cashCollected:0,discountsGiven:0,tips:0};staff[v.exitStaffId].exits++;if(v.paymentMethod==='cash')staff[v.exitStaffId].cashCollected+=v.fee||0;if(v.discountAmount>0)staff[v.exitStaffId].discountsGiven++;}});getTips().forEach(t=>{if(staff[t.staffId])staff[t.staffId].tips+=t.amount||0;});return Object.values(staff);};
export const getCustomerStats = (plate) => {const history=getVehicles().filter(v=>v.plate.toLowerCase()===plate.toLowerCase()&&v.status!=='parked');return{visits:history.length,totalSpend:history.reduce((s,v)=>s+(v.fee||0),0),avgDuration:0,lastVisit:history[history.length-1]?.entryTime||null};};

// ── WHATSAPP ──────────────────────────────────────────────
export const buildWhatsAppMessage = (type,data) => {
  const st=getSettings();const sep='━━━━━━━━━━━━━━━━━━━';
  if(type==='entry'){return encodeURIComponent(`🅿️ *${st.businessName}*\n${st.address}\n\n📋 *ENTRY RECEIPT*\n${sep}\nDate:     ${data.date}\nEntry:    ${data.entryTime}\nPlate:    ${data.plate}\nPackage:  ${data.packageType}\n${data.rate?`Rate:     AED ${data.rate}/hour\n`:''}${data.advance?`Advance:  AED ${data.advance} paid\n`:''}${sep}\n*Terms:*\n1. AED ${st.hourlyRate}/hr. First hour full rate.\n2. Grace ${st.overtimeBufferMins}min = AED ${st.overtimeBufferFee} only. After ${st.overtimeBufferMins} mins full hour (AED ${st.hourlyRate}) charged.\n3. No liability for loss/damage/theft.\n${sep}\n${st.receiptFooter}`);}
  return encodeURIComponent(`🅿️ *${st.businessName}*\n${st.address}\n\n🧾 *EXIT RECEIPT*\n${sep}\nDate:     ${data.date}\nEntry:    ${data.entryTime}\nExit:     ${data.exitTime}\nDuration: ${data.duration}\nPlate:    ${data.plate}\nPackage:  ${data.packageType}\n${sep}\n${data.advance?`Advance Paid: AED ${data.advance}\n`:''}${data.extra?`Extra Hours:  AED ${data.extra}\n`:''}${data.discount?`Discount:     -AED ${data.discount}\n`:''}*TOTAL:   AED ${data.fee}*\n${data.balance?`*BALANCE DUE: AED ${data.balance}*\n`:''}Payment:  ${data.method}\nStaff:    ${data.staff}\n${sep}\n${st.receiptFooter}`);
};
export const buildReminderMessage = (tenant) => {
  const st=getSettings();
  const expiry=tenant.expiryDate||tenant.expiry_date?new Date(tenant.expiryDate||tenant.expiry_date).toLocaleDateString('en-GB'):'—';
  return encodeURIComponent(`🅿️ *${st.businessName}*\n\nDear ${tenant.name},\n\nYour parking package expires on *${expiry}*.\n\nPlease renew at the parking office to avoid disruption.\n\nThank you 🙏`);
};
